import React, { useRef, useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Segment } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import CardCommon from '@op/components/CardCommon';
import ExportTable from '@intelligence/components/ExportTable';
import ChartSection from '../ChartSection';
import TopPublishersTable, { DEFAULT_PAGE_SIZE } from '../TopPublishersTable';
import ExpandedPublisherGames from '../ExpandedPublisherGames';
import useCancelRequest, { IRequestToken } from '@intelligence/hooks/useCancelRequest';
import useLocalTableState, { createDefaultSorts } from '../../hooks/useLocalTableState';
import {
  fetchTopPublishers,
  fetchTopPublishersChart,
  fetchTopPublishersGames,
  fetchGamesDetails,
} from '../../services';
import { getAdjustedRevenueValue } from '@intelligence/utils/revenueScope';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import { filterRevenueMetrics, formatTooltipTimeRange, getSortOrderBys } from '../../utils';
import {
  buildAggregatedChartData,
  buildLineChartData,
  createMetricNameFormatter,
  createXAxisLabelFormatter,
  createChartTooltipFormatters,
} from '../../utils/chartDataBuilder';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbhBtnLogMes } from '@home/utils/buriedPointCfg';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { ChartType } from '@intelligence/v3/enums/chart';
import {
  ENTITY_TYPE,
  KEY_GAMES_TAB_OPTIONS,
  TOP_PUBLISHERS_KPI_TIPS_I18N,
  getDataSourceCustomFieldKey,
  getDataSourceMetricKey,
  getDataSourceMetricRatioKey,
  getDataSourceMetrics,
  getMetricOrderBy,
  getPublisherGameMetrics,
  getPublisherKpiMetrics,
} from '../../constants';

import type { ICasualCompetitorsDataSourceProps, ICasualCompetitorsParams } from '../../types';

import './index.scss';

export interface ITopPublishersInstance {
  update: (params: ICasualCompetitorsParams) => void;
}

interface IPublisherData extends IRecord {
  companyId: string;
  companyName: string;
  companyLogo: string;
  companyCountry: string;
  companyCountryAbbre: string;
  companyGameCount: number;
  dau: number;
  dauRatio: number;
  iapRevenue: number;
  iapRevenueRatio: number;
  download: number;
  downloadRatio: number;
}

interface ILineChartDataItem {
  date: string;
  value: number;
  name: string;
  company_id?: string;
  payload?: IRecord;
}

interface ITopPublishersV2Props extends IBaseProps, ICasualCompetitorsDataSourceProps {
  topPublishersTableTipsI18n?: IRecord<IRecord<string>>;
  topPublisersKpiTipsI18n?: IRecord<{
    dau: string;
    iap: string;
    download: string;
  }>;
  params: ICasualCompetitorsParams | null;
  genreOptionsMap?: IRecord<any[]>;
}

const DEFAULT_METRIC_TAB = 'daily_avg_dau';
const PUBLISHER_CHART_METRIC_NAMES = ['daily_avg_dau', 'iap_revenue', 'download'];
const CHART_MAX_PUBLISHERS = 5;

const topPublishersDownloadCfg = dbhBtnLogMes.studioCasualHubCompetitorsTopPublishersDownload;
const topPublishersExpandCfg = dbhBtnLogMes.studioCasualHubCompetitorsTopPublishersExpand;

const TopPublishersV2: React.ForwardRefRenderFunction<ITopPublishersInstance, ITopPublishersV2Props> = (props, ref) => {
  const {
    style,
    className,
    topPublishersTableTipsI18n,
    topPublisersKpiTipsI18n = TOP_PUBLISHERS_KPI_TIPS_I18N,
    params,
    dataSource,
  } = props;
  const { locale, formatMessage } = useIntl();
  const { next: nextRequest } = useCancelRequest();
  const dauMetricKey = getDataSourceMetricKey(dataSource, 'daily_avg_dau');
  const dauRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'daily_avg_dau');
  const iapMetricKey = getDataSourceMetricKey(dataSource, 'iap_revenue');
  const iapRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'iap_revenue');
  const downloadMetricKey = getDataSourceMetricKey(dataSource, 'download');
  const downloadRatioMetricKey = getDataSourceMetricRatioKey(dataSource, 'download');
  const subGenreField = getDataSourceCustomFieldKey(dataSource, 'sub_genre');
  const publisherChartMetrics = getDataSourceMetrics(dataSource, PUBLISHER_CHART_METRIC_NAMES);
  const publisherKpiMetrics = getPublisherKpiMetrics(dataSource);
  const publisherGameMetrics = getPublisherGameMetrics(dataSource);

  const latestParamsRef = useRef<ICasualCompetitorsParams | null>(null);
  const requestVersionRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [lineChartLoading, setLineChartLoading] = useState(false);
  const [chartType, setChartType] = useState<ChartType>(ChartType.Line);
  const [metricTab, setMetricTab] = useState(DEFAULT_METRIC_TAB);
  const currentMetricKey = getDataSourceMetricKey(dataSource, metricTab);
  const [publishersData, setPublishersData] = useState<IPublisherData[]>([]);
  const [checkedCompanyIds, setCheckedCompanyIds] = useState<string[]>([]);
  const [lineChartDataMap, setLineChartDataMap] = useState<Record<string, ILineChartDataItem[]>>({});

  // 行展开相关状态
  const [expandedCompanyIds, setExpandedCompanyIds] = useState<string[]>([]);
  const [companyGamesMap, setCompanyGamesMap] = useState<Record<string, IRecord[]>>({});
  const [companyGamesLoading, setCompanyGamesLoading] = useState<Record<string, boolean>>({});
  const [pageCompanyGameIdsMap, setPageCompanyGameIdsMap] = useState<Record<string, string[]>>({});

  const { sorts, pagingQuery, handleSortsChange, handlePagingChange, resetState, getSortedData } =
    useLocalTableState<IPublisherData>({
      defaultSorts: createDefaultSorts(dataSource, 'daily_avg_dau'),
      defaultPageSize: DEFAULT_PAGE_SIZE,
    });

  const hasRevenue = params?.showRevenue ?? true;

  const filteredMetricOptions = useMemo(() => {
    return KEY_GAMES_TAB_OPTIONS.filter((item) => {
      return !(item.value === 'iap_revenue' && !hasRevenue);
    });
  }, [hasRevenue]);

  const sortedPublishersData = useMemo(() => {
    return getSortedData(publishersData);
  }, [getSortedData, publishersData]);

  const currentPageCompanyIds = sortedPublishersData.map((item) => item?.companyId).filter(isNotEmpty);

  const isCompanyGameOrderReady =
    isNotEmpty(currentPageCompanyIds) &&
    currentPageCompanyIds.every((companyId) => Array.isArray(pageCompanyGameIdsMap?.[companyId]));

  const granularity = latestParamsRef.current?.granularity || 'daily';

  const getMetricName = createMetricNameFormatter(granularity, {
    daily: {
      daily_avg_dau: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_daily_dau' }),
      iap_revenue: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_daily_iap' }),
      download: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_daily_download' }),
    },
    monthly: {
      daily_avg_dau: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_monthly_dau' }),
      iap_revenue: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_monthly_iap' }),
      download: formatMessage({ id: 'dbh_studio_casual_publisher_kpi_monthly_download' }),
    },
  });

  const xAxisLabelFormatter = createXAxisLabelFormatter(granularity);
  const tooltipTimeRange = formatTooltipTimeRange(params?.granularity, params?.startDate, params?.endDate);

  const getMetricValue = (publisher: IPublisherData, metricKey: string): number => {
    if (metricKey === 'daily_avg_dau') {
      return publisher.dau;
    }
    if (metricKey === 'iap_revenue') {
      return publisher.iapRevenue;
    }

    return publisher.download;
  };

  const getTopPublisherIds = (
    data: IPublisherData[],
    currentSorts?: IRatioMetricSortState<IPublisherData>[],
  ): string[] => {
    if (isEmpty(data)) {
      return [];
    }

    const sortedData = isEmpty(currentSorts)
      ? [...data].sort((a, b) => (b?.dau ?? 0) - (a?.dau ?? 0))
      : multiRatioMetricSort([...data], currentSorts || []);

    return sortedData
      .slice(0, CHART_MAX_PUBLISHERS)
      .map((item) => item?.companyId)
      .filter(Boolean);
  };

  const getSortedGameIdsForDownload = (games: IRecord[], sortState?: IRatioMetricSortState<IRecord>): string[] => {
    if (isEmpty(games)) {
      return [];
    }

    const sortStates = sortState?.type ? [sortState] : createDefaultSorts<IRecord>(dataSource, 'daily_avg_dau');

    return multiRatioMetricSort([...games], sortStates)
      .map((item) => item?.game_id)
      .filter(isNotEmpty);
  };

  const buildCompanyGameIdsMap = (gamesByCompany: Record<string, IRecord[]>, companyIds: string[]) => {
    const nextCompanyGameIdsMap: Record<string, string[]> = {};

    companyIds.forEach((companyId) => {
      nextCompanyGameIdsMap[companyId] = getSortedGameIdsForDownload(gamesByCompany[companyId] || []);
    });

    return nextCompanyGameIdsMap;
  };

  const syncCompanyGameIds = (companyId: string, games: IRecord[], sortState?: IRatioMetricSortState<IRecord>) => {
    if (!companyId) {
      return;
    }

    setPageCompanyGameIdsMap((prev) => ({
      ...prev,
      [companyId]: getSortedGameIdsForDownload(games, sortState),
    }));
  };

  /**
   * 获取排序后的第一个厂商ID
   */
  const getFirstCompanyId = (
    data: IPublisherData[],
    currentSorts?: IRatioMetricSortState<IPublisherData>[],
  ): string => {
    if (isEmpty(data)) {
      return '';
    }
    const sorted = isEmpty(currentSorts)
      ? [...data].sort((a, b) => (b?.dau ?? 0) - (a?.dau ?? 0))
      : multiRatioMetricSort([...data], currentSorts || []);
    return sorted[0]?.companyId || '';
  };

  /**
   * 懒加载厂商游戏数据
   * @description 传入所有厂商 ID 以确保 dau_share 计算基数与初版一致，
   *   响应按 company_id 分组后一次性缓存所有厂商的游戏数据。
   * @param companyId 当前需要展开的厂商 ID（用于控制 loading 状态）
   * @param allPublishers 可选，所有厂商数据；未传时使用 publishersData 状态
   */
  const fetchCompanyGames = async (companyId: string, allPublishers?: IPublisherData[]) => {
    if (!companyId) {
      return;
    }

    const currentParams = latestParamsRef.current;
    if (!currentParams) {
      return;
    }

    const publishers = allPublishers || publishersData;
    const allCompanyIds = publishers.map((p) => p.companyId).filter(isNotEmpty);
    if (isEmpty(allCompanyIds)) {
      return;
    }

    const version = requestVersionRef.current;
    setCompanyGamesLoading((prev) => ({ ...prev, [companyId]: true }));

    try {
      const gameMetrics = filterRevenueMetrics(publisherGameMetrics, currentParams.showRevenue);
      const gamesRes = await fetchTopPublishersGames({
        start_date: currentParams.startDate,
        end_date: currentParams.endDate,
        granularity: currentParams.granularity,
        metrics: gameMetrics,
        markets: currentParams.markets,
        platforms: currentParams.platforms,
        revenue_scope: currentParams.revenueScope,
        order_by: [getMetricOrderBy(dataSource, 'daily_avg_dau')],
        filter: {
          genre_id: currentParams.genreId,
          company_ids: allCompanyIds,
        },
      });

      if (version !== requestVersionRef.current) {
        return;
      }

      const allGamesList = (gamesRes as any)?.data?.list || [];
      const allGameIds = allGamesList.map((g: any) => g?.game_id).filter(isNotEmpty);
      let gamesDetailsMap: IRecord = {};

      if (isNotEmpty(allGameIds)) {
        try {
          gamesDetailsMap = await fetchGamesDetails(allGameIds, [
            'entity_name',
            'cover',
            'publisher',
            'country_abbre',
            subGenreField,
          ]);

          if (version !== requestVersionRef.current) {
            return;
          }
        } catch (detailsError) {
          console.debug('[TopPublishersV2] fetchGamesDetails error:', detailsError);

          if (version !== requestVersionRef.current) {
            return;
          }
        }
      }

      // 按 company_id 分组并一次性缓存所有厂商的游戏数据
      const gamesByCompany: Record<string, IRecord[]> = {};
      allGamesList.forEach((game: any) => {
        const cid = game?.company_id;
        if (!cid) {
          return;
        }
        if (!gamesByCompany[cid]) {
          gamesByCompany[cid] = [];
        }
        const gameId = game?.game_id || '';
        const details = gamesDetailsMap?.[gameId];
        gamesByCompany[cid].push({
          ...game,
          name: details?.entity_name || '',
          cover: details?.cover || '',
          publisher: details?.publisher || '',
          publisher_country_abbre: details?.country_abbre || '',
          sub_genre: details?.[subGenreField] || '',
        });
      });

      setCompanyGamesMap((prev) => {
        const next = { ...prev };
        allCompanyIds.forEach((cid) => {
          next[cid] = gamesByCompany[cid] || [];
        });
        return next;
      });
      setPageCompanyGameIdsMap(buildCompanyGameIdsMap(gamesByCompany, allCompanyIds));
    } catch (error) {
      console.debug('[TopPublishersV2] fetchCompanyGames error:', error);

      if (version !== requestVersionRef.current) {
        return;
      }
      setCompanyGamesMap((prev) => ({ ...prev, [companyId]: [] }));
    } finally {
      if (version === requestVersionRef.current) {
        setCompanyGamesLoading((prev) => ({ ...prev, [companyId]: false }));
      }
    }
  };

  /**
   * 自动展开第一个厂商并加载其游戏数据
   */
  const autoExpandFirstPublisher = (data: IPublisherData[], currentSorts?: IRatioMetricSortState<IPublisherData>[]) => {
    const firstId = getFirstCompanyId(data, currentSorts);
    if (firstId) {
      setExpandedCompanyIds([firstId]);
      fetchCompanyGames(firstId, data);
    } else {
      setExpandedCompanyIds([]);
    }
  };

  const reportExpandedPublisherLog = (keys: string[]) => {
    const expandedCompanyIdsMap = new Map(publishersData.map((item) => [item.companyId, item]));
    const nextExpandedCompanyIds = keys.filter((companyId) => !expandedCompanyIds.includes(companyId));

    nextExpandedCompanyIds.forEach((companyId) => {
      const companyInfo = expandedCompanyIdsMap.get(companyId);
      if (!companyInfo) {
        return;
      }

      buriedBtnPoint({
        ...topPublishersExpandCfg,
        extInfo: `${companyInfo.companyName}(${companyInfo.companyId})`,
      });
    });
  };

  /**
   * 处理表格行展开/收起
   */
  const handleExpandedKeysChange = (keys: string[]) => {
    reportExpandedPublisherLog(keys);
    setExpandedCompanyIds(keys);
    keys.forEach((companyId) => {
      if (!companyGamesMap[companyId]) {
        fetchCompanyGames(companyId);
      }
    });
  };

  /**
   * 渲染展开行内容
   */
  const renderExpandedRow = (record: IPublisherData) => {
    const { companyId } = record;
    const isGamesLoading = companyGamesLoading[companyId] && !companyGamesMap[companyId];
    const games = companyGamesMap[companyId] || [];

    return (
      <ExpandedPublisherGames
        loading={isGamesLoading}
        topPublishersTableTipsI18n={topPublishersTableTipsI18n}
        dataSource={dataSource}
        games={games}
        hasRevenue={hasRevenue}
        granularity={params?.granularity}
        startDate={params?.startDate}
        endDate={params?.endDate}
        genreOptionsMap={props.genreOptionsMap}
        onSortStateChange={(nextSortState) => {
          syncCompanyGameIds(companyId, games, nextSortState);
        }}
      />
    );
  };

  const fetchPublisherLineChartData = async (
    currentParams: ICasualCompetitorsParams,
    publisherList: IPublisherData[],
    version: number,
    requestToken: IRequestToken,
  ) => {
    const companyIds = publisherList.map((item) => item.companyId).filter(Boolean);
    if (isEmpty(companyIds)) {
      setLineChartDataMap({});
      return;
    }

    setLineChartLoading(true);

    try {
      const metrics = filterRevenueMetrics(publisherChartMetrics, currentParams.showRevenue);
      const res = await fetchTopPublishersChart(
        {
          start_date: currentParams.chartStartDate,
          end_date: currentParams.endDate,
          granularity: currentParams.granularity,
          metrics,
          markets: currentParams.markets,
          platforms: currentParams.platforms,
          revenue_scope: currentParams.revenueScope,
          order_by: [],
          filter: {
            genre_id: currentParams.genreId,
            company_ids: companyIds,
          },
        },
        { cancelToken: requestToken.cancelToken },
      );

      if (version !== requestVersionRef.current || requestToken.isStale()) {
        return;
      }

      const entityInfoMap = (res as any)?.ext?.entity_info || {};
      const list = (res as any)?.data?.list || [];
      const nextLineChartDataMap: Record<string, ILineChartDataItem[]> = {};

      metrics.forEach((metricKey) => {
        nextLineChartDataMap[metricKey] = list.map((item: any) => {
          const companyId = item?.company_id || '';
          const entityInfo = entityInfoMap?.[companyId] || {};

          return {
            date: item?.date || '',
            value: item?.[metricKey] ?? 0,
            name: entityInfo?.name || companyId,
            company_id: companyId,
            payload: {
              iconUrl: entityInfo?.image_url || '',
              iconAlt: entityInfo?.name || companyId,
            },
          };
        });
      });

      setLineChartDataMap(nextLineChartDataMap);
    } catch (error) {
      if (version !== requestVersionRef.current || requestToken.isStale()) {
        return;
      }
      setLineChartDataMap({});
    } finally {
      if (version === requestVersionRef.current && !requestToken.isStale()) {
        setLineChartLoading(false);
      }
    }
  };

  const fetchPublishersData = async (
    currentParams: ICasualCompetitorsParams,
    requestToken: IRequestToken,
    version: number,
  ) => {
    const metrics = filterRevenueMetrics(publisherKpiMetrics, currentParams.showRevenue);

    setLoading(true);

    try {
      const res = await fetchTopPublishers(
        {
          start_date: currentParams.startDate,
          end_date: currentParams.endDate,
          granularity: currentParams.granularity,
          metrics,
          markets: currentParams.markets,
          platforms: currentParams.platforms,
          revenue_scope: currentParams.revenueScope,
          order_by: [getMetricOrderBy(dataSource, 'daily_avg_dau')],
          filter: {
            genre_id: currentParams.genreId,
          },
        },
        { cancelToken: requestToken.cancelToken },
      );

      if (version !== requestVersionRef.current || requestToken.isStale()) {
        return;
      }

      const entityInfoMap = (res as any)?.ext?.entity_info || {};
      const list = (res as any)?.data?.list || [];
      const nextPublishersData = list.map((item: any) => {
        const companyId = item?.company_id || '';
        const entityInfo = entityInfoMap?.[companyId] || {};

        return {
          companyId,
          companyName: entityInfo?.name || '',
          companyLogo: entityInfo?.image_url || '',
          companyCountry: entityInfo?.country_name_en || '',
          companyCountryAbbre: entityInfo?.country_abbre || '',
          companyGameCount: item?.company_game_count ?? 0,
          [dauMetricKey]: item?.[dauMetricKey] ?? 0,
          [dauRatioMetricKey]: item?.[dauRatioMetricKey] ?? 0,
          dau: item?.[dauMetricKey] ?? 0,
          dauRatio: item?.[dauRatioMetricKey] ?? 0,
          dauPrev: item?.[`${dauMetricKey}_prev`] ?? 0,
          [iapMetricKey]: item?.[iapMetricKey] ?? 0,
          [iapRatioMetricKey]: item?.[iapRatioMetricKey] ?? 0,
          iapRevenue: item?.[iapMetricKey] ?? 0,
          iapRevenueRatio: item?.[iapRatioMetricKey] ?? 0,
          iapRevenuePrev: item?.[`${iapMetricKey}_prev`] ?? 0,
          [downloadMetricKey]: item?.[downloadMetricKey] ?? 0,
          [downloadRatioMetricKey]: item?.[downloadRatioMetricKey] ?? 0,
          download: item?.[downloadMetricKey] ?? 0,
          downloadRatio: item?.[downloadRatioMetricKey] ?? 0,
          downloadPrev: item?.[`${downloadMetricKey}_prev`] ?? 0,
        };
      });

      setPublishersData(nextPublishersData);
      setCheckedCompanyIds(getTopPublisherIds(nextPublishersData, createDefaultSorts(dataSource, 'daily_avg_dau')));
      autoExpandFirstPublisher(nextPublishersData, createDefaultSorts(dataSource, 'daily_avg_dau'));
      fetchPublisherLineChartData(currentParams, nextPublishersData, version, requestToken);
    } catch (error) {
      if (version !== requestVersionRef.current || requestToken.isStale()) {
        return;
      }
      setPublishersData([]);
      setPageCompanyGameIdsMap({});
      setLineChartDataMap({});
    } finally {
      if (version === requestVersionRef.current && !requestToken.isStale()) {
        setLoading(false);
      }
    }
  };

  /**
   * Bar/Pie 聚合数据
   */
  const aggregatedChartData = useMemo(() => {
    return buildAggregatedChartData({
      sortedData: sortedPublishersData,
      checkedIds: checkedCompanyIds,
      maxItems: CHART_MAX_PUBLISHERS,
      getId: (item) => item.companyId,
      getName: (item) => item.companyName || item.companyId,
      getValue: (item) => getMetricValue(item, metricTab),
      getIconUrl: (item) => item.companyLogo || '',
    });
  }, [checkedCompanyIds, metricTab, sortedPublishersData]);

  const barChartData = useMemo(() => {
    return aggregatedChartData.map((item) => ({
      name: item.name,
      value: item.value,
      payload: item.payload,
    }));
  }, [aggregatedChartData]);

  const pieChartData = useMemo(() => {
    return aggregatedChartData.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.value,
      payload: item.payload,
    }));
  }, [aggregatedChartData]);

  /**
   * 折线图数据
   */
  const lineChartData = useMemo(() => {
    const rawData = lineChartDataMap[currentMetricKey] || [];
    if (isEmpty(sortedPublishersData)) {
      return [];
    }

    return buildLineChartData({
      rawData,
      checkedIds: checkedCompanyIds,
      totalCount: sortedPublishersData.length,
      maxItems: CHART_MAX_PUBLISHERS,
      getDate: (item) => item.date,
      getEntityId: (item) => item.company_id || '',
      getName: (item) => item.name,
      getValue: (item) => item.value,
      getIconUrl: (item) => item?.payload?.iconUrl || '',
    }).map((item) => ({
      ...item,
      company_id: item.entityId,
    }));
  }, [checkedCompanyIds, currentMetricKey, lineChartDataMap, sortedPublishersData]);

  const { lineTooltipFormatter, barTooltipFormatter, pieTooltipFormatter } = createChartTooltipFormatters({
    getMetricName,
    metricTab,
    timeRange: tooltipTimeRange,
    xAxisLabelFormatter,
  });

  /**
   * 获取下载请求参数
   */
  const getDownloadReqParams = () => {
    if (!params) {
      return {};
    }

    const companyIds = currentPageCompanyIds;
    const gameMetrics = filterRevenueMetrics(publisherGameMetrics, hasRevenue);
    const companyMetrics = filterRevenueMetrics(publisherKpiMetrics, hasRevenue);
    const orderBy = getSortOrderBys(sorts?.[0] as any, dataSource);
    const adjustedRevenueFilter = (() => {
      const revenueScope = params?.revenueScope;

      if (isEmpty(revenueScope)) {
        return {};
      }

      return {
        adjusted_revenue: getAdjustedRevenueValue(revenueScope),
      };
    })();

    return {
      start_date: params.startDate,
      end_date: params.endDate,
      granularity: params.granularity,
      metrics: gameMetrics,
      company_metrics: companyMetrics,
      order_by: orderBy,
      markets: params.markets,
      platforms: params.platforms,
      filter: {
        ...adjustedRevenueFilter,
        genre_id: params.genreId,
        company_ids: companyIds,
      },
      hasRevenue,
      page_company_ids: currentPageCompanyIds,
      page_company_game_ids_map: pageCompanyGameIdsMap,
    };
  };

  useImperativeHandle(ref, () => ({
    update: (nextParams: ICasualCompetitorsParams) => {
      latestParamsRef.current = nextParams;
      requestVersionRef.current += 1;

      if (!nextParams.showRevenue && metricTab === 'iap_revenue') {
        setMetricTab(DEFAULT_METRIC_TAB);
      }

      setPublishersData([]);
      setCheckedCompanyIds([]);
      setLineChartDataMap({});
      setLineChartLoading(false);
      setExpandedCompanyIds([]);
      setCompanyGamesMap({});
      setCompanyGamesLoading({});
      setPageCompanyGameIdsMap({});
      resetState();

      fetchPublishersData(nextParams, nextRequest(), requestVersionRef.current);
    },
  }));

  return (
    <CardCommon
      style={style}
      className={classnames('dbh-card-table dbh-panel-top-publishers-v2', className)}
      title={formatMessage({ id: 'dbh_studio_casual_top_publishers_title' })}
      // @ts-ignore
      operationInfo={[chartType, setChartType]}
      operations={[
        {
          icon: 'column-chart',
          symbol: ChartType.Bar,
        },
        {
          icon: 'discount-chart',
          symbol: ChartType.Line,
        },
        {
          icon: 'pie-chart-icon',
          symbol: ChartType.Pie,
        },
        {
          symbol: 'download',
          element: (
            <ExportTable
              useWebDownload
              downloadModalSize={800}
              entityType={ENTITY_TYPE}
              businessType="casualCompetitorsTopPublishers"
              downloadKey="casualCompetitorsTopPublishers"
              downloadLogConfig={topPublishersDownloadCfg}
              newStyleFileName="Publishers"
              stickText={formatMessage({
                id: 'dbi_mobile_download_modal_stick_tips',
              })}
              total={MAX_ROWS_LIMIT}
              isDisableDownload={isEmpty(publishersData) || !params || !isCompanyGameOrderReady}
              tableParams={getDownloadReqParams()}
            />
          ),
        },
      ]}
      handleOperationsOnClick={(item) => {
        if (item.symbol) {
          setChartType(item.symbol as ChartType);
        }
      }}
    >
      <div className="dbh-panel-top-publishers-v2-metric-row">
        <Segment
          options={filteredMetricOptions.map((item) => ({
            text: formatMessage({ id: item.text }),
            value: item.value,
          }))}
          value={metricTab}
          onChange={(value) => setMetricTab(value)}
        />
      </div>

      {loading ? (
        <Loading style={{ height: 720 }} />
      ) : isEmpty(publishersData) ? (
        <NoData style={{ height: 720 }} />
      ) : (
        <>
          <ChartSection
            className="dbh-panel-top-publishers-v2-chart"
            lineLoading={lineChartLoading}
            height={320}
            locale={locale as any}
            chartType={chartType}
            metricName={getMetricName(metricTab)}
            barChartData={barChartData}
            pieChartData={pieChartData}
            lineChartData={lineChartData}
            barTooltipFormatter={barTooltipFormatter}
            pieTooltipFormatter={pieTooltipFormatter}
            lineTooltipFormatter={lineTooltipFormatter}
            xAxisLabelFormatter={xAxisLabelFormatter}
          />

          <TopPublishersTable
            loading={loading}
            dataSource={dataSource}
            granularity={params?.granularity}
            startDate={params?.startDate}
            endDate={params?.endDate}
            hasRevenue={hasRevenue}
            data={publishersData}
            showChecker
            sorts={sorts}
            onSortsChange={(nextSorts) => {
              const nextMetric = nextSorts?.[0]?.metric;
              if (nextMetric && nextMetric !== metricTab) {
                setMetricTab(nextMetric);
              }
              handleSortsChange(nextSorts);
              setCheckedCompanyIds(getTopPublisherIds(publishersData, nextSorts));
              // 排序变化时，清空游戏缓存并自动展开第一个厂商
              setCompanyGamesMap({});
              setCompanyGamesLoading({});
              setPageCompanyGameIdsMap({});
              autoExpandFirstPublisher(publishersData, nextSorts);
            }}
            pagingQuery={pagingQuery}
            onPagingChange={handlePagingChange}
            checkedList={checkedCompanyIds}
            onCheckedListChange={setCheckedCompanyIds}
            kpiTipsI18nKeys={topPublisersKpiTipsI18n?.[params?.granularity || 'monthly']}
            expandedKeys={expandedCompanyIds}
            onExpandedKeysChange={handleExpandedKeysChange}
            renderExpandedRow={renderExpandedRow}
          />
        </>
      )}
    </CardCommon>
  );
};

// @ts-ignore
export default React.forwardRef(TopPublishersV2);
